import type { ReactNode } from 'react';
import { Sparkles, Lightbulb } from 'lucide-react';
import { TEAL_TEXT } from '../constants';
import type { StageVM } from '../types';
import { useCognition } from '../context';

import { CognitionTaskUnderstandingTable } from '../cognition-task-understanding-table';
import { CognitionDomainTable } from '../cognition-domain-table';
import { RetrievalStrategiesTable } from '../tables/retrieval-strategies-table';
import { RawAttributeExtractionTable } from '../tables/raw-attribute-extraction-table';
import { CompetitiveComparisonTable } from '../tables/competitive-comparison-table';
import { ConsensusIndicator } from '../consensus-indicator';
import { SynthesisCards } from './synthesis-cards';

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

// Expanded stage panel (DC §7.3): finding lede, why-this-matters, the REUSED
// detail tables, variance callouts, key takeaway, and Ask-Upmode doorways.
export function StageDetail({
  stage,
  selectedModelIds,
  onAsk,
}: {
  stage: StageVM;
  selectedModelIds: string[];
  onAsk: (question: string) => void;
}) {
  const { runResult, modelLabel } = useCognition();

  return (
    <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* finding lede */}
      {stage.lede?.headline && (
        <div
          style={{
            background: 'linear-gradient(135deg,var(--alpha-teal-5),transparent 72%)',
            border: '1px solid var(--alpha-teal-20)',
            borderRadius: 10,
            padding: '16px 18px',
          }}
        >
          <p style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.45, color: 'var(--foreground)', margin: '0 0 11px' }}>
            {stage.lede.headline}
          </p>
          {stage.lede.meta.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {stage.lede.meta.map((mt, i) => (
                <span
                  key={i}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 24,
                    padding: '0 11px',
                    borderRadius: 6,
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--muted-foreground)',
                  }}
                >
                  {mt}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* why this matters */}
      {stage.whyMatters && (
        <div
          style={{
            background: 'var(--spectral-violet-10)',
            border: '1px solid var(--spectral-violet-20)',
            borderRadius: 10,
            padding: '15px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 8,
              background: 'var(--card)',
              color: 'var(--spectral-violet)',
              flexShrink: 0,
            }}
          >
            <Sparkles style={{ width: 15, height: 15 }} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--spectral-violet)', marginBottom: 4 }}>
              Why this matters
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--foreground)', margin: 0 }}>{stage.whyMatters}</p>
          </div>
        </div>
      )}

      {/* per-stage detail tables (existing components, reused) */}
      {stage.id === 'task-setup' && (
        <>
          <Block title="How models understood the task">
            <CognitionTaskUnderstandingTable
              rows={runResult?.taskSetupData || []}
              modelIds={selectedModelIds}
              modelLabel={modelLabel}
            />
          </Block>
          <Block title="Retrieval strategies">
            <RetrievalStrategiesTable
              rows={runResult?.retrievalStrategiesData || []}
              fusionOrder={runResult?.retrievalOrderData || []}
              modelIds={selectedModelIds}
              modelLabel={modelLabel}
            />
          </Block>
        </>
      )}

      {stage.id === 'evidence-signals' && (
        <>
          <Block title="Domain attribution">
            <CognitionDomainTable rows={runResult?.domainData || []} />
          </Block>
          <Block title="Raw attribute extraction">
            <RawAttributeExtractionTable
              rows={runResult?.attributeData || []}
              modelIds={selectedModelIds}
              modelLabel={modelLabel}
              renderConsensus={(c) => <ConsensusIndicator consensus={c} />}
            />
          </Block>
          {stage.variances && stage.variances.length > 0 && (
            <Block title="Biggest variances">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {stage.variances.map((v, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: 11,
                      alignItems: 'flex-start',
                      padding: '12px 14px',
                      border: '1px solid var(--border)',
                      borderLeft: `3px solid ${v.color}`,
                      borderRadius: 10,
                      background: 'var(--card)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)', flexShrink: 0, minWidth: 120 }}>
                      {v.label}
                    </span>
                    <span style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--muted-foreground)' }}>{v.detail}</span>
                  </div>
                ))}
              </div>
            </Block>
          )}
        </>
      )}

      {stage.id === 'understanding-decisions' && (
        <Block title="Competitive comparison">
          <CompetitiveComparisonTable rows={(runResult?.competitiveComparisonData || []) as any[]} />
        </Block>
      )}

      {stage.id === 'final-synthesis' && (
        <Block title="Per-model synthesis">
          <SynthesisCards rows={runResult?.responseConstructionData || []} modelLabel={modelLabel} />
        </Block>
      )}

      {/* key takeaway */}
      {stage.takeaway && (
        <div
          style={{
            background: 'var(--alpha-teal-5)',
            border: '1px solid var(--alpha-teal-20)',
            borderRadius: 10,
            padding: '14px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 8,
              background: 'var(--alpha-teal-10)',
              color: TEAL_TEXT,
              flexShrink: 0,
            }}
          >
            <Lightbulb style={{ width: 14, height: 14 }} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: TEAL_TEXT, marginBottom: 4 }}>Key takeaway</div>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--foreground)', margin: 0 }}>{stage.takeaway}</p>
          </div>
        </div>
      )}

      {/* ask-upmode doorways */}
      {stage.asks.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: 'var(--spectral-violet)' }}>
            <span
              style={{
                width: 15,
                height: 15,
                borderRadius: 99,
                background: 'linear-gradient(135deg,var(--spectral-violet),var(--signal-blue))',
                flexShrink: 0,
              }}
            />
            Ask Upmode
          </span>
          {stage.asks.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onAsk(q)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 30,
                padding: '0 13px',
                borderRadius: 99,
                background: 'var(--card)',
                border: '1px solid var(--border)',
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--foreground)',
                fontFamily: 'inherit',
                cursor: 'pointer',
                transition: 'border-color var(--duration-base) var(--ease-standard), color var(--duration-base) var(--ease-standard)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--spectral-violet)';
                e.currentTarget.style.color = 'var(--spectral-violet)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--foreground)';
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
